/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { ReceiptSummary } from "../Purchases/types";
import { money } from "./format";

type Props = {
  /** This invoice's id (null while new). */
  invoiceId: number | null;
  supplierId: number | null;
  receipts: ReceiptSummary[];
  selected: number[];
  readOnly: boolean;
  onChange: (ids: number[]) => void;
};

// Pick the deliveries this invoice bills: the supplier's deliveries that no
// other invoice bills yet. Shows how the delivered total compares.
export const LinkedDeliveries = defineComponent<Props>({
  name: "LinkedDeliveries",
  props: ["invoiceId", "supplierId", "receipts", "selected", "readOnly", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const candidates = computed(() =>
      props.receipts.filter(
        (r) =>
          props.selected.includes(r.id) ||
          (!props.readOnly &&
            r.supplier_id === props.supplierId &&
            (r.supplier_invoice_id == null || r.supplier_invoice_id === props.invoiceId)),
      ),
    );
    const deliveredTotal = computed(() =>
      props.receipts.filter((r) => props.selected.includes(r.id)).reduce((s, r) => s + r.total, 0),
    );

    const toggle = (id: number) =>
      props.onChange(
        props.selected.includes(id) ? props.selected.filter((x) => x !== id) : [...props.selected, id],
      );

    return () => (
      <div class="rounded-lg border border-base-300">
        <div class="flex items-center justify-between border-b border-base-300 px-3 py-2">
          <span class="text-sm font-medium">{t("inventory.invoices.deliveries")}</span>
          {props.selected.length > 0 && (
            <span class="text-xs text-base-content/60">
              {t("inventory.invoices.delivered_total").replace("{total}", money(deliveredTotal.value))}
            </span>
          )}
        </div>
        {!props.supplierId ? (
          <p class="px-3 py-3 text-xs text-base-content/50">{t("inventory.invoices.deliveries_pick_supplier")}</p>
        ) : candidates.value.length === 0 ? (
          <p class="px-3 py-3 text-xs text-base-content/50">{t("inventory.invoices.deliveries_none")}</p>
        ) : (
          <ul class="max-h-48 divide-y divide-base-200 overflow-auto">
            {candidates.value.map((r) => (
              <li key={r.id}>
                <label class="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-base-200/60">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-sm"
                    disabled={props.readOnly}
                    checked={props.selected.includes(r.id)}
                    onChange={() => toggle(r.id)}
                  />
                  <span class="flex-1 text-sm">
                    {new Date(r.received_at).toLocaleDateString()}
                    {r.reference && <span class="ml-2 text-base-content/50">{r.reference}</span>}
                  </span>
                  {r.status === "draft" && (
                    <span class="badge badge-ghost badge-sm">{t("inventory.purchases.status_draft")}</span>
                  )}
                  <span class="text-sm font-medium">{money(r.total)}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
});
